import { InputRichText } from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { conversationLanguages } from 'lib-shared-languages';
import { Channel } from 'lib-shared-types-channels';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useUnmount } from 'react-use';

import { AIImproveWritingPromptName, aiApi } from 'apis/ai';
import { useQuickAiDraft } from 'components/editorCopilotDropdown/hooks/useQuickAiDraft';
import { pubSub } from 'utils/pubSub';
import { useEditorContext } from 'utils/useEditorContext';
import { translationFailText } from 'utils/useTranslateText';

import { copilotActionRegistry } from './copilotActions.registry';
import {
  resolveCopy,
  type CopilotActionContext,
  type CopilotActionDefinition,
  type CopilotActionId,
  type CopilotOfferedImprovement,
  type CopilotStatus,
} from './copilotActions.types';
import { resolveImprovements } from './resolveImprovements';
import { UIBlockerError } from './uiBlockerError';
import { UIBlockerLoading } from './uiBlockerLoading';
import { useLastInboundMessage } from './useLastInboundMessage';

/** The subset of an RTK Query trigger the runner relies on. */
type CopilotTrigger = {
  abort: () => void;
  unwrap: () => Promise<{ text?: string }>;
};

/** Per-invocation values for actions whose prompt is chosen by the user. */
export type CopilotRunOptions = {
  promptName?: AIImproveWritingPromptName;
  includeContext?: boolean;
};

type UseCopilotActionRunnerOptions = {
  context: CopilotActionContext;
  /** Live editor text, used for disabled state only. */
  text: string;
  channel?: Channel;
  csid?: string;
  /** Conversation language code, used by quick translate. */
  languageCode?: string;
  /**
   * Whether to cancel an in-flight request when the owner unmounts. Defaults to
   * true, which is right when the owner lives as long as the editor. The
   * copilot dropdown's panels set it to false: they close the dropdown as soon
   * as they start, which unmounts them, and the request must outlive that.
   */
  abortOnUnmount?: boolean;
};

type UseCopilotActionRunnerResult = {
  run: (id: CopilotActionId, options?: CopilotRunOptions) => void;
  /** Disabled state and tooltip, or undefined for actions without a status. */
  getStatus: (id: CopilotActionId) => CopilotStatus | undefined;
  /** The action's name for this context, honouring any override. */
  resolveName: (id: CopilotActionId) => string;
  /** Improvements for the Improve action, excluding inapplicable ones. */
  improvements: CopilotOfferedImprovement[];
};

const useHasStyleGuideForChannel = (
  channel: Channel | undefined,
  skip: boolean,
): boolean => {
  const { data } = aiApi.useReadAIWritingStyleGuideMatchQuery(
    {
      features: 'impr_reply',
      ...(channel && {
        channels: convertChannelCasing(channel, 'snake') as Channel,
      }),
    },
    { skip: skip || !channel, refetchOnMountOrArgChange: true },
  );

  return !!data?.data?.match;
};

/**
 * Executes copilot actions from the registry: resolves the editor selection,
 * paints the loading and error overlays, inserts the result and aborts any
 * in-flight request on unmount. Also resolves each action's disabled state.
 */
export const useCopilotActionRunner = ({
  context,
  text,
  channel,
  csid,
  languageCode,
  abortOnUnmount = true,
}: UseCopilotActionRunnerOptions): UseCopilotActionRunnerResult => {
  const { content } = useContext(InputRichText.Context);
  const { replace, insertBelow, getSelectedText } = useEditorContext();
  const lastMessage = useLastInboundMessage();
  const triggerRef = useRef<CopilotTrigger | null>(null);
  const [runningActionId, setRunningActionId] = useState<CopilotActionId>();

  const [improveWriting] = aiApi.useCreateAiWritingImprovementMutation();
  const [createTranslation] = aiApi.useCreateAiTextTranslationMutation();
  const [checkSpellingAndGrammar] =
    aiApi.useLazyReadAiCheckGrammarAndSpellingQuery();

  const includes = useCallback(
    (id: CopilotActionId) => context.actions.includes(id),
    [context.actions],
  );

  // Both the dedicated action and improveReply's repeat-input need this.
  const hasStyleGuideForChannel = useHasStyleGuideForChannel(
    channel,
    !includes('improveReplyMatchStyleGuide') && !includes('improveReply'),
  );

  const improvements = useMemo(
    () =>
      resolveImprovements({
        excluded: context.excludedImprovements,
        hasStyleGuideForChannel,
      }),
    [context.excludedImprovements, hasStyleGuideForChannel],
  );

  const languageName = useMemo(() => {
    const language = conversationLanguages.find((lng) =>
      languageCode?.startsWith(lng.code),
    );
    return language?.name ?? '';
  }, [languageCode]);

  const snakeChannel = useMemo(
    () =>
      channel ? (convertChannelCasing(channel, 'snake') as Channel) : undefined,
    [channel],
  );

  const clearBlocker = useCallback(() => {
    content.setUiBlocker(null);
  }, [content]);

  const showLoading = useCallback(
    (entry: CopilotActionDefinition) => {
      content.setUiBlocker(
        <UIBlockerLoading
          loadingText={resolveCopy(entry.loadingText, { languageName })}
        />,
      );
    },
    [content, languageName],
  );

  const showError = useCallback(
    (entry: CopilotActionDefinition) => {
      content.setUiBlocker(
        <UIBlockerError
          onClick={clearBlocker}
          errorText={resolveCopy(entry.errorText, { languageName })}
        />,
      );
    },
    [clearBlocker, content, languageName],
  );

  const insertResult = useCallback(
    (entry: CopilotActionDefinition, result: string) => {
      if (entry.insert === 'insertBelow') {
        insertBelow(result);
        return;
      }
      replace(result);
    },
    [insertBelow, replace],
  );

  const draftEntry = copilotActionRegistry.quickAiDraft;
  const handleDraftInserted = useCallback(() => {
    setRunningActionId(undefined);
    clearBlocker();
  }, [clearBlocker]);
  const handleDraftError = useCallback(() => {
    setRunningActionId(undefined);
    showError(draftEntry);
  }, [draftEntry, showError]);

  const {
    generateDraft,
    hasAgent,
    isLoading: isDraftLoading,
  } = useQuickAiDraft({
    dixaConversationId: csid ?? '',
    insert: draftEntry.insert,
    onInserted: handleDraftInserted,
    onError: handleDraftError,
  });

  useUnmount(() => {
    if (abortOnUnmount) {
      triggerRef.current?.abort();
    }
  });

  const resolveName = useCallback(
    (id: CopilotActionId) =>
      context.names?.[id] ?? copilotActionRegistry[id].name,
    [context.names],
  );

  const getStatus = useCallback(
    (id: CopilotActionId): CopilotStatus | undefined => {
      const entry = copilotActionRegistry[id];

      return entry.getStatus?.({
        text,
        name: resolveName(id),
        isLoading:
          runningActionId === id || (id === 'quickAiDraft' && isDraftLoading),
        channel,
        hasStyleGuideForChannel,
        hasLanguage: !!languageName,
        hasAgent,
        csid,
      });
    },
    [
      channel,
      csid,
      hasAgent,
      hasStyleGuideForChannel,
      isDraftLoading,
      languageName,
      resolveName,
      runningActionId,
      text,
    ],
  );

  const run = useCallback(
    (id: CopilotActionId, options: CopilotRunOptions = {}) => {
      const entry = copilotActionRegistry[id];
      const { execution } = entry;

      // Opens the action's panel in the copilot dropdown on the next tick, so
      // the click does not reach the dropdown's outside-click listener.
      if (execution.kind === 'delegateToMenu') {
        setTimeout(() => {
          pubSub.publish(`command:${entry.commandName}`);
        });
        return;
      }

      if (execution.kind === 'agentDraft') {
        setRunningActionId(id);
        showLoading(entry);
        generateDraft();
        return;
      }

      const selectedText = getSelectedText();
      let trigger: CopilotTrigger;

      switch (execution.kind) {
        case 'writingImprovement': {
          const promptName = options.promptName ?? execution.promptName;
          if (!promptName) {
            return;
          }
          const includeContext =
            options.includeContext ?? execution.includeContext;

          trigger = improveWriting({
            lastMessage,
            text: selectedText,
            promptName,
            contexts: includeContext
              ? {
                  features: 'impr_reply',
                  ...(snakeChannel && { channels: snakeChannel }),
                }
              : undefined,
            csid,
          });
          break;
        }
        case 'translateToConversationLanguage':
          trigger = createTranslation({
            language: languageName,
            text: selectedText,
            csid,
            direction: 'outbound',
          });
          break;
        case 'spellingGrammar':
          trigger = checkSpellingAndGrammar({ text: selectedText, csid });
          break;
      }

      setRunningActionId(id);
      showLoading(entry);
      triggerRef.current = trigger;

      trigger
        .unwrap()
        .then((data) => {
          triggerRef.current = null;
          setRunningActionId(undefined);

          if (!data?.text || data.text.includes(translationFailText)) {
            showError(entry);
            return;
          }

          insertResult(entry, data.text);
          clearBlocker();
        })
        .catch(() => {
          triggerRef.current = null;
          setRunningActionId(undefined);
          showError(entry);
        });
    },
    [
      checkSpellingAndGrammar,
      clearBlocker,
      createTranslation,
      csid,
      generateDraft,
      getSelectedText,
      improveWriting,
      insertResult,
      languageName,
      lastMessage,
      showError,
      showLoading,
      snakeChannel,
    ],
  );

  return { run, getStatus, resolveName, improvements };
};
